import React, { useEffect, useState } from 'react';

// Floating "back to top" control for the dashboard's hub pages (10-28).
//
// The dashboard <main> is overflow-hidden and each page scrolls its own
// container (HubPageLayout), so this scrolls that element, not the window.
// Appears once the reader is a screen or so down, sits above the Report rail,
// and is a real button so it is reachable with the keyboard.
const SHOW_AFTER_PX = 400;

// Scrolls whichever element scrolls the page: the hub layout marks its
// container with data-hub-scroll; outside one (the admin's preview) fall
// back to the window.
const scrollToTop = (from) => {
  const el = from?.closest?.('[data-hub-scroll]');
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior = reduce ? 'auto' : 'smooth';
  if (el) el.scrollTo({ top: 0, behavior });
  else window.scrollTo({ top: 0, behavior });
};

// Inline "Back to top" row for the foot of an article (10-29): a reader who
// has just finished the page gets the control where their eyes are, not only
// as the floating button in the corner.
export const BackToTopLink = ({ className = '' }) => (
  <div className={`mt-10 border-t border-gray-300 pt-6 text-center ${className}`}>
    <button
      type="button"
      onClick={(event) => scrollToTop(event.currentTarget)}
      className="inline-flex items-center gap-2 text-sm text-black hover:text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
    >
      <span aria-hidden="true">&uarr;</span> Back to top
    </button>
  </div>
);

const BackToTop = ({ scrollRef }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => setVisible(el.scrollTop > SHOW_AFTER_PX);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  const toTop = () => {
    const el = scrollRef.current;
    if (!el) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  };

  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={toTop}
      aria-label="Back to top"
      title="Back to top"
      className="fixed bottom-6 right-6 z-30 flex h-11 w-11 items-center justify-center rounded-full border-2 border-black bg-white text-xl text-black shadow-md transition-colors hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
    >
      <span aria-hidden="true">&uarr;</span>
    </button>
  );
};

export default BackToTop;
